import { useRef, useEffect, useCallback, useState } from 'react';
import { getRequest } from '../../services/httpService';
import i18n from '../../services/i18n';

// ----------------------
// QR Code Scanner Utilities
// ----------------------

/**
 * Styles for hidden QR input field - positioned off-screen but still focusable
 */
export const QRHiddenFieldSx = {
  position: 'absolute',
  left: '-9999px',
  top: 'auto',
  width: '1px',
  height: '1px',
  padding: '0',
  margin: '0',
  border: 'none',
  background: 'transparent',
  color: 'transparent',
  caretColor: 'transparent',
  outline: 'none',
};

/**
 * QR Scan API payload interface
 */
export interface ScanBarcodePayload {
  barcode_text: string;
}

/**
 * QR Scan API response interface
 */
export interface QRScanPharmacyResponse {
  stock_transaction_id: number;
  date: string;
  item_code: string;
  item_name: string;
  batch_code: string;
  expiry_date: string;
  item_group: string;
  item_category: string;
  is_free: boolean;
  opening_stock: string;
  used_stock: string;
  addition_stock: string;
  closing_stock: string;
  base_cost: string;
  total_cost: string;
  base_mrp: string;
  total_mrp: string;
  item_id: number;
  base_uom_id: number;
  base_uom_name: string;
  is_low_stock: boolean;
  is_no_stock: boolean;
  is_near_expiry: boolean;
  clinic_name: string;
  clinic_id: number;
  store_name: string;
  store_id: number;
  batch_id: number;
  sgst_percent?: number;
  cgst_percent?: number;
  igst_percent?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uom_object?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  batch_object?: any[];
}

/**
 * Scan barcode/QR API call
 */
export function scanBarcodeQR(data: ScanBarcodePayload): Promise<QRScanPharmacyResponse> {
  const url = `/batch-barcode-scan/?barcode_text=${data.barcode_text}`;
  return getRequest(url, { responseType: 'json' });
}

/**
 * Hook options for useQRCodeScanner
 */
export interface UseQRCodeScannerOptions {
  isActive: boolean;
  clinicId?: number | null;
  storeId?: number | null;
  onScanSuccess: (response: QRScanPharmacyResponse) => void;
  onScanError: (error: Error | string) => void;
  onClinicMismatch?: () => void;
  onStoreMismatch?: () => void;
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
}

/**
 * Return type for useQRCodeScanner hook
 */
export interface UseQRCodeScannerReturn {
  scanInputRef: React.RefObject<HTMLInputElement>;
  scanInputValue: string;
  setScanInputValue: React.Dispatch<React.SetStateAction<string>>;
  handleScanSubmit: () => void;
}

/**
 * Reusable QR Code Scanner Hook
 * Provides input management, auto-focus behavior, and scan API integration
 */
export function useQRCodeScanner({
  isActive,
  clinicId,
  storeId,
  onScanSuccess,
  onScanError,
  onClinicMismatch,
  onStoreMismatch,
  onLoadStart,
  onLoadEnd,
}: UseQRCodeScannerOptions): UseQRCodeScannerReturn {
  const tBilling = (key: string, defaultValue: string) => i18n.t(key, { ns: 'billing_config', defaultValue });
  const scanInputRef = useRef<HTMLInputElement>(null);
  const [scanInputValue, setScanInputValue] = useState('');

  // Auto-focus management when scan mode is active
  useEffect(() => {
    if (isActive) {
      // Focus immediately
      scanInputRef.current?.focus();

      // Check if current element is an allowed input (qty, UOM, package fields)
      const isAllowedInput = () => {
        const activeEl = document.activeElement;
        if (!activeEl) return false;
        
        // Allow interaction with select dropdowns, number inputs, and autocomplete
        const tagName = activeEl.tagName.toLowerCase();
        if (tagName === 'select') return true;
        if (tagName === 'input') {
          const inputType = (activeEl as HTMLInputElement).type;
          // Allow number inputs (qty), text inputs in autocomplete
          if (inputType === 'number' || inputType === 'text') {
            // Check if it's not the scan input itself
            return activeEl !== scanInputRef.current;
          }
        }
        // Allow clicking on menu items, options, etc.
        if (activeEl.closest('[role="listbox"]') || activeEl.closest('[role="option"]')) {
          return true;
        }
        return false;
      };

      // Handle blur events to refocus (only if not in allowed input)
      const handleBlur = (event: FocusEvent) => {
        if (event.target === scanInputRef.current && isActive) {
          setTimeout(() => {
            if (isActive && !isAllowedInput()) {
              scanInputRef.current?.focus();
            }
          }, 100);
        }
      };

      const scanInput = scanInputRef.current;
      scanInput?.addEventListener('blur', handleBlur);

      // Fallback interval check - only refocus if not in allowed input
      const focusInterval = setInterval(() => {
        if (
          scanInputRef.current &&
          document.activeElement !== scanInputRef.current &&
          isActive &&
          !isAllowedInput()
        ) {
          scanInputRef.current.focus();
        }
      }, 500);

      return () => {
        scanInput?.removeEventListener('blur', handleBlur);
        clearInterval(focusInterval);
      };
    } else {
      scanInputRef.current?.blur();
    }
  }, [isActive]);

  const handleScanSubmit = useCallback(() => {
    const value = scanInputRef.current?.value?.trim() || scanInputValue.trim();
    
    if (!value) {
      onScanError(tBilling('pharmacy_dispense.errors.scan_barcode_required', 'Please scan a barcode'));
      scanInputRef.current?.focus();
      return;
    }

    const payload: ScanBarcodePayload = { barcode_text: value };

    onLoadStart?.();

    scanBarcodeQR(payload)
      .then((response) => {
        // Validate clinic match if clinicId is set
        if (clinicId && response.clinic_id !== clinicId) {
          setScanInputValue('');
          if (onClinicMismatch) {
            onClinicMismatch();
          } else {
            onScanError(tBilling('pharmacy_dispense.errors.scanned_item_clinic_mismatch_selected', 'Scanned item clinic does not match selected clinic'));
          }
          return;
        }

        // Validate store match if storeId is set
        if (storeId && response.store_id !== storeId) {
          setScanInputValue('');
          if (onStoreMismatch) {
            onStoreMismatch();
          } else {
            onScanError(tBilling('pharmacy_dispense.errors.scanned_item_store_mismatch_selected', 'Scanned item store does not match selected store'));
          }
          return;
        }

        onScanSuccess(response);
        setScanInputValue('');
      })
      .catch((error) => {
        onScanError(error.message || tBilling('pharmacy_dispense.errors.qr_scan_failed', 'An error occurred while scanning the QR code'));
        setScanInputValue('');
        setTimeout(() => {
          scanInputRef.current?.focus();
        }, 100);
      })
      .finally(() => {
        onLoadEnd?.();
      });
  }, [scanInputValue, clinicId, storeId, onScanSuccess, onScanError, onClinicMismatch, onStoreMismatch, onLoadStart, onLoadEnd]);

  return {
    scanInputRef,
    scanInputValue,
    setScanInputValue,
    handleScanSubmit,
  };
}

// ----------------------
// Clinical Patients (Pharmacy Dispense Module)
// ----------------------

export interface PartnerInfo {
  id: number;
  age: number;
  created_on: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender__name: string;
  medical_record_no: string;
  mobile_number: string;
  alternate_email: string | null;
  is_lead: boolean;
  photo: string | null;
  type: string | null;
  gender__name_label: string;
}

export interface ClinicalInvoicePatient {
  id: number;
  appointment_date?: string;
  first_name: string;
  last_name?: string;
  medical_record_no?: string;
  gender__name_label?: string | null;
  mobile_number?: string;
  treatment_doctor?: string;
  partner?: PartnerInfo | null;
}

export interface ClinicalInvoicePatientsMeta {
  limit: number;
  next: string | null;
  offset: number;
  previous: string | null;
  total_count: number;
}

export interface ClinicalInvoicePatientsResponse {
  meta: ClinicalInvoicePatientsMeta;
  objects: ClinicalInvoicePatient[];
}



export interface ClinicalInvoicePatientsFilters {
  clinic: number;
  from_date: string; // YYYY-MM-DD
  to_date: string;   // YYYY-MM-DD
  limit?: number;
  offset?: number;
  search?: string;
}


function clinicalPatientsFiltersToParams(filters: ClinicalInvoicePatientsFilters): URLSearchParams {
  const {
    clinic,
    from_date,
    to_date,
    limit = 10,
    offset = 0,
    search,
  } = filters;

  const params = new URLSearchParams({
    checked_in: 'true',
    patient_type: 'patient',
    from_date,
    to_date,
    limit: String(limit),
    offset: String(offset),
    clinic: String(clinic),
  });

  if (search) params.set('search', search);

  return params;
}

/**
 * Fetches clinical patients for invoice creation using the
 * /api/invoices/clinical-patients/ endpoint.
 *
 * Example URL:
 * /api/invoices/clinical-patients/?checked_in=true&patient_type=patient&from_date=2025-12-16&to_date=2025-12-16&limit=10&offset=0&clinic=7
 */
export function fetchClinicalInvoicePatients(
  filters: ClinicalInvoicePatientsFilters
): Promise<ClinicalInvoicePatientsResponse> {
  const params = clinicalPatientsFiltersToParams(filters);
  return getRequest(`pharmacy-dispensary/clinical-patients/?${params.toString()}`);
}